/**
 * Page de détails d'un client PWA pour un cyber
 * Affiche tous les dossiers et événements du client
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Warning,
  HourglassEmpty,
  Schedule,
  Sync,
  Visibility,
  VisibilityOff,
  Save,
} from '@mui/icons-material';
import axios from 'axios';

interface Dossier {
  id: number;
  numero_demande?: string;
  type_demande?: string;
  anef_statut_actuel?: string;
  anef_date_depot?: string;
  anef_derniere_mise_a_jour?: string;
  anef_dernier_titre?: string;
  is_active: boolean;
  unread_events_count: number;
  anef_details_complets?: any;
}

interface Event {
  id: number;
  event_type: string;
  title: string;
  description?: string;
  event_date: string;
  notification_read: boolean;
  numero_demande?: string;
  type_demande?: string;
}

interface ClientDetails {
  relationship_id: number;
  client_account_id: number;
  anef_identifiant: string;
  client_nom: string;
  client_prenom: string;
  client_email: string;
  client_telephone?: string;
  suivi_active: boolean;
  total_dossiers: number;
  unread_events_count: number;
  notes_cyber?: string;
}

export default function CyberClientPWADetails() {
  const { clientAccountId } = useParams<{ clientAccountId: string }>();
  const navigate = useNavigate();

  const [clientDetails, setClientDetails] = useState<ClientDetails | null>(null);
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [recentEvents, setRecentEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    if (clientAccountId) {
      fetchClientDetails();
    }
  }, [clientAccountId]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/cyber-clients/${clientAccountId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = response.data.data;
      setClientDetails(data.client);
      setDossiers(data.dossiers || []);
      setRecentEvents(data.recent_events || []);
      setNotes(data.client.notes_cyber || '');
    } catch (error: any) {
      console.error('Erreur chargement détails:', error);
      alert(error.response?.data?.message || 'Erreur lors du chargement');
      navigate('/cyber-clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/cyber-clients/${clientAccountId}/notes`,
        { notes },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert('✅ Notes enregistrées');
    } catch (error: any) {
      console.error('Erreur sauvegarde notes:', error);
      alert(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleViewDossier = (dossierId: number) => {
    // Ouvrir la vue détaillée du dossier
    window.open(`/cyber-clients/${clientAccountId}/dossiers/${dossierId}`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!clientDetails) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">Client non trouvé</Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* En-tête */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/cyber-clients')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {clientDetails.client_prenom} {clientDetails.client_nom}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {clientDetails.anef_identifiant}
            </Typography>
          </Box>
          <Chip
            label={clientDetails.suivi_active ? 'Suivi actif' : 'Suivi désactivé'}
            color={clientDetails.suivi_active ? 'success' : 'default'}
            icon={clientDetails.suivi_active ? <Visibility /> : <VisibilityOff />}
          />
        </Box>

        {/* Informations client */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Informations du client
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">{clientDetails.client_email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Téléphone
                  </Typography>
                  <Typography variant="body2">
                    {clientDetails.client_telephone || 'Non renseigné'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Dossiers actifs
                  </Typography>
                  <Typography variant="body2">{clientDetails.total_dossiers}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Événements non lus
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    {clientDetails.unread_events_count}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Notes privées
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                placeholder="Ajoutez des notes sur ce client..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                size="small"
                sx={{ mb: 1 }}
              />
              <Button
                fullWidth
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveNotes}
                disabled={savingNotes}
              >
                Enregistrer
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Onglets */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label={`Dossiers (${dossiers.length})`} />
            <Tab label={`Événements récents (${recentEvents.length})`} />
          </Tabs>
        </Box>

        {/* Contenu selon l'onglet */}
        {tabValue === 0 && (
          <>
            {dossiers.length === 0 ? (
              <Alert severity="info">Aucun dossier actif pour ce client</Alert>
            ) : (
              <Grid container spacing={2}>
                {dossiers.map((dossier) => {
                  const hasAlerts = dossier.unread_events_count > 0;
                  return (
                    <Grid item xs={12} md={6} lg={4} key={dossier.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 4 },
                          borderLeft: '4px solid',
                          borderColor: hasAlerts ? 'warning.main' : 'primary.main',
                        }}
                        onClick={() => handleViewDossier(dossier.id)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'start', mb: 1 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: hasAlerts
                                  ? 'warning.main'
                                  : dossier.anef_dernier_titre
                                  ? 'success.main'
                                  : 'info.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 1.5,
                              }}
                            >
                              {hasAlerts ? (
                                <Warning sx={{ color: 'white' }} />
                              ) : dossier.anef_dernier_titre ? (
                                <CheckCircle sx={{ color: 'white' }} />
                              ) : (
                                <HourglassEmpty sx={{ color: 'white' }} />
                              )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {dossier.type_demande || 'Démarche ANEF'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                N° {dossier.numero_demande?.slice(-8) || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            <Chip
                              size="small"
                              label={dossier.anef_statut_actuel || 'En cours'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                            {dossier.unread_events_count > 0 && (
                              <Chip
                                size="small"
                                label={`${dossier.unread_events_count} nouveau(x)`}
                                color="error"
                                sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                              />
                            )}
                          </Box>

                          {dossier.anef_date_depot && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              📅 Dépôt: {new Date(dossier.anef_date_depot).toLocaleDateString('fr-FR')}
                            </Typography>
                          )}
                          {dossier.anef_derniere_mise_a_jour && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              🔄 MAJ: {new Date(dossier.anef_derniere_mise_a_jour).toLocaleDateString('fr-FR')}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}

        {tabValue === 1 && (
          <>
            {recentEvents.length === 0 ? (
              <Alert severity="info">Aucun événement récent</Alert>
            ) : (
              <Paper>
                <List>
                  {recentEvents.map((event, index) => (
                    <React.Fragment key={event.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {event.title}
                              </Typography>
                              {!event.notification_read && (
                                <Chip label="Nouveau" color="error" size="small" sx={{ height: 20 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              {event.description && (
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                  {event.description}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                🗂️ {event.numero_demande} • {event.type_demande}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Schedule sx={{ fontSize: 14 }} />
                                {new Date(event.event_date).toLocaleString('fr-FR')}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                      {index < recentEvents.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </>
        )}
      </Container>
    </Layout>
  );
}
